import { createSlice } from '@reduxjs/toolkit'

import { loadCachedFeedbacks, loadFeedbacks, sendFeedback } from '../actions/feedback'
import type { AppState } from '../types'
import type { Feedback, SendStatus } from '../types/Feedback'
import type { ActionStatus } from '../types/Input'

export type FeedbackSliceState = {
  byTargetId: Record<string, Feedback>
  loadStatus: ActionStatus
  send: Record<string, SendStatus>
}

export const initialState: FeedbackSliceState = {
  byTargetId: {},
  loadStatus: 'none',
  send: {},
}

const byTargetId = (feedbacks: Feedback[]): Record<string, Feedback> =>
  Object.fromEntries(feedbacks.map((feedback) => [feedback.targetId, feedback]))

export const feedbackSlice = createSlice({
  name: 'feedbacks',
  initialState,
  reducers: {
    clearFeedbacks: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCachedFeedbacks.fulfilled, (state, { payload }) => {
        Object.assign(state.byTargetId, byTargetId(payload))
      })
      .addCase(loadFeedbacks.pending, (state) => {
        state.loadStatus = 'loading'
      })
      .addCase(loadFeedbacks.fulfilled, (state, { payload }) => {
        state.loadStatus = 'succeeded'
        state.byTargetId = byTargetId(payload)
      })
      .addCase(loadFeedbacks.rejected, (state) => {
        state.loadStatus = 'failed'
      })
      .addCase(sendFeedback.pending, (state, { meta }) => {
        state.send[meta.arg.targetId] = { status: 'loading' }
      })
      .addCase(sendFeedback.fulfilled, (state, { payload }) => {
        state.byTargetId[payload.targetId] = payload
        state.send[payload.targetId] = { status: 'succeeded' }
      })
      .addCase(sendFeedback.rejected, (state, { meta, payload, error }) => {
        const { targetId } = meta.arg
        if (state.byTargetId[targetId] && state.send[targetId]?.status === 'succeeded') return
        state.send[targetId] = { status: 'failed', error: payload ?? error.message }
      })
  },
})

export const { clearFeedbacks } = feedbackSlice.actions

const notSent: SendStatus = { status: 'none' }

export const selectFeedback = (
  state: AppState,
  targetId: string | undefined
): Feedback | undefined => (targetId ? state.feedbacks.byTargetId[targetId] : undefined)

export const selectSendStatus = (state: AppState, targetId: string | undefined): SendStatus =>
  (targetId && state.feedbacks.send[targetId]) || notSent

export default feedbackSlice.reducer
